/**
 * Status Bar Bridge
 * 
 * Controls device status bar appearance.
 * PARTIAL on web - can control browser theme-color, but not full status bar.
 */

import { BaseBridge } from '../BaseBridge'
import type { BridgeAvailability, BridgeCapabilities } from '../types'

export class StatusBarBridge extends BaseBridge {
  readonly bridgeName = 'StatusBar'
  readonly availability: BridgeAvailability = 'partial'
  readonly methods = ['setColor', 'setStyle', 'hide', 'show']
  
  private originalThemeColor: string | null = null
  
  async initialize(): Promise<void> {
    // Store original theme color for restoration
    const themeColorMeta = document.querySelector('meta[name="theme-color"]')
    this.originalThemeColor = themeColorMeta?.getAttribute('content') || null
  }
  
  async handleCall(method: string, args: any[]): Promise<any> {
    switch (method) {
      case 'setColor':
        return this.setThemeColor(args[0])
      case 'setStyle':
        return this.setStyle(args[0])
      case 'hide':
        // Can't hide browser chrome, no-op
        return true
      case 'show':
        // Can't control browser chrome, no-op
        return true
      default:
        throw this.createMethodNotFoundError(method)
    }
  }
  
  private setThemeColor(color: string): boolean {
    if (!color) return false
    
    let themeColorMeta = document.querySelector('meta[name="theme-color"]')
    
    if (!themeColorMeta) {
      themeColorMeta = document.createElement('meta')
      themeColorMeta.setAttribute('name', 'theme-color')
      document.head.appendChild(themeColorMeta)
    }
    
    themeColorMeta.setAttribute('content', color)
    return true
  }
  
  private setStyle(style: 'light' | 'dark'): boolean {
    // Update color-scheme meta tag
    let colorSchemeMeta = document.querySelector('meta[name="color-scheme"]')
    
    if (!colorSchemeMeta) {
      colorSchemeMeta = document.createElement('meta')
      colorSchemeMeta.setAttribute('name', 'color-scheme')
      document.head.appendChild(colorSchemeMeta)
    }
    
    colorSchemeMeta.setAttribute('content', style)
    return true
  }
  
  cleanup(): void {
    // Restore original theme color
    if (this.originalThemeColor !== null) {
      const themeColorMeta = document.querySelector('meta[name="theme-color"]')
      if (themeColorMeta) {
        themeColorMeta.setAttribute('content', this.originalThemeColor)
      }
    }
  }
  
  getCapabilities(): BridgeCapabilities {
    return {
      setColor: { 
        available: true,
        fallback: 'Changes browser theme-color only'
      },
      setStyle: { 
        available: true,
        fallback: 'Changes color-scheme meta only'
      },
      hide: { 
        available: false,
        fallback: 'Cannot hide browser UI'
      },
      show: { 
        available: false,
        fallback: 'Cannot control browser UI'
      }
    }
  }
}

